import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getAnalyticsOverview, getAgents, getConversations } from '../lib/api';
import { Bot, MessageSquare, Zap, Plus, ArrowRight, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const getAgentBackendName = (agent) => agent?.name || agent?.display_name_effective || agent?.display_name || 'Agent';
const getAgentDisplayName = (agent) => agent?.display_name_effective || agent?.display_name || agent?.name || 'Agent';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({ agents: 0, conversations: 0, messages: 0 });
  const [agents, setAgents] = useState([]);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, agentsRes, convsRes] = await Promise.all([
          getAnalyticsOverview(),
          getAgents(),
          getConversations()
        ]);
        setAnalytics(analyticsRes.data);
        setAgents(agentsRes.data.slice(0, 3));
        setRecentConversations(convsRes.data.slice(0, 5));
      } catch (e) {
        console.error('Failed to fetch dashboard data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: 'Active Agents', value: analytics.agents, icon: Bot, gradient: 'from-[#9333EA] to-[#A855F7]' },
    { label: 'Conversations', value: analytics.conversations, icon: MessageSquare, gradient: 'from-[#EC4899] to-[#F472B6]' },
    { label: 'Messages', value: analytics.messages, icon: Zap, gradient: 'from-[#9333EA] to-[#EC4899]' },
  ];

  const getCategoryLabel = (category) => {
    const labels = {
      customer_service: 'Customer Service',
      appointment_setting: 'Appointment Setting',
      sales: 'Sales',
      routing: 'Routing Agent'
    };
    return labels[category] || category;
  };

  const getCategoryClass = (category) => {
    const classes = {
      customer_service: 'badge-customer-service',
      appointment_setting: 'badge-appointment-setting',
      sales: 'badge-sales',
      routing: 'badge-routing'
    };
    return classes[category] || '';
  };

  const openCreateAgentDialog = () => setCreateDialogOpen(true);
  const handleCreateConversationalAgent = () => {
    setCreateDialogOpen(false);
    navigate('/agents/new');
  };
  const handleCreateAgentEmployee = () => {
    setCreateDialogOpen(false);
    navigate('/agent-employees/new');
  };

  return (
    <Layout>
      <div className="p-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Monitor your primary business agents, conversations, and deployment surfaces.</p>
          </div>
          <Button className="btn-gradient text-white rounded-lg gap-2" data-testid="create-agent-button" onClick={openCreateAgentDialog}>
            <Plus className="w-4 h-4" />
            Add new agent
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <div 
              key={stat.label}
              className="apollo-card p-6 animate-fade-in"
              style={{ animationDelay: `${i * 0.1}s` }}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">{stat.label}</p>
                  <p className="text-3xl font-semibold mt-1 font-['Outfit']">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Agents and Conversations Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Agents */}
          <div className="apollo-card">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold font-['Outfit']">Your Agent Brains</h2>
              <Link to="/agents" className="text-sm text-[#9333EA] hover:text-[#EC4899] transition-colors flex items-center gap-1" data-testid="view-all-agents">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {loading ? (
                <div className="p-4 text-center text-muted-foreground">Loading...</div>
              ) : agents.length === 0 ? (
                <div className="p-8 text-center">
                  <Bot className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">No agent brain yet</p>
                  <Button variant="outline" className="rounded-lg gap-2" data-testid="create-first-agent" onClick={openCreateAgentDialog}>
                    <Plus className="w-4 h-4" />
                    Add new agent
                  </Button>
                </div>
              ) : (
                agents.map((agent) => (
                  <Link 
                    key={agent.id} 
                    to={`/agents/${agent.id}`}
                    className="p-4 flex items-center justify-between hover:bg-secondary transition-colors"
                    data-testid={`agent-row-${agent.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#9333EA]/20 to-[#EC4899]/20 rounded-lg flex items-center justify-center">
                        <Bot className="w-5 h-5 text-[#9333EA]" />
                      </div>
                      <div>
                        <p className="font-medium">{getAgentBackendName(agent)}</p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryClass(agent.category)}`}>
                          {getCategoryLabel(agent.category)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`status-dot ${agent.is_active ? 'active' : 'inactive'}`}></span>
                      <span className="text-sm text-muted-foreground">{agent.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Recent Conversations */}
          <div className="apollo-card">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold font-['Outfit']">Recent Conversations</h2>
              <Link to="/conversations" className="text-sm text-[#9333EA] hover:text-[#EC4899] transition-colors flex items-center gap-1" data-testid="view-all-conversations">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {loading ? (
                <div className="p-4 text-center text-muted-foreground">Loading...</div>
              ) : recentConversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No conversations yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Conversations will appear here when visitors interact with your agents</p>
                </div>
              ) : (
                recentConversations.map((conv) => (
                  <div 
                    key={conv.id} 
                    className="p-4 flex items-center justify-between hover:bg-secondary transition-colors"
                    data-testid={`conversation-row-${conv.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#EC4899]/20 to-[#9333EA]/20 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-[#EC4899]" />
                      </div>
                      <div>
                        <p className="font-medium">Visitor {conv.visitor_id?.slice(0, 8)}</p>
                        <p className="text-sm text-muted-foreground capitalize">{conv.channel} • {conv.status}</p>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(conv.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add new agent</DialogTitle>
              <DialogDescription>
                Choose the kind of agent you want to create in Apollo.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={handleCreateConversationalAgent}
                className="rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold">Conversational agent</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Build a customer-facing agent for chat, websites, channels, and scheduling flows.
                </p>
              </button>
              <button
                type="button"
                onClick={handleCreateAgentEmployee}
                className="rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Briefcase className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold">Agent employee</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Configure an execution agent that works in the runtime system, claims tasks, and drives real execution.
                </p>
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DashboardPage;
